import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';

const UploadBox = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
  transition: 'border-color 0.3s ease',
  '&:hover': { borderColor: theme.palette.primary.main },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '140px',
}));

const AudioPreviewContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const DeleteButton = styled(Button)({
  position: 'absolute',
  top: '8px',
  right: '8px',
  minWidth: 'unset',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
});

export interface UploadedAudio {
  file: File;
  preview: string;
}

export interface AudioUploaderProps {
  uploadedAudio: UploadedAudio | null;
  onUpload: (acceptedFiles: File[]) => void;
  onRemove: () => void;
}

const AudioUploader: React.FC<AudioUploaderProps> = ({ uploadedAudio, onUpload, onRemove }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg'] },
    maxFiles: 1,
    onDrop: onUpload,
  });

  return (
    <>
      {uploadedAudio ? (
        <AudioPreviewContainer>
          <audio src={uploadedAudio.preview} controls style={{ width: '100%' }} />
          <DeleteButton variant="contained" color="error" size="small" onClick={onRemove} startIcon={<DeleteIcon />}>
            Remove
          </DeleteButton>
        </AudioPreviewContainer>
      ) : (
        <UploadBox {...getRootProps()}>
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            {isDragActive ? 'Drop the audio here' : 'Drag & drop an audio file here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select a file
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Supported: MP3, WAV, M4A, AAC, FLAC, OGG
          </Typography>
        </UploadBox>
      )}
    </>
  );
};

export default AudioUploader;
